import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DateTimeRangePicker, type DateRange } from "@/components/ui/datetime-range-picker";
import { Select, SelectItem } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";
import statisticsAPIService from "@/lib/statistics-api.service";

export type StatisticsFilterType =
	| "total"
	| "enrollment_status"
	| "payment_status"
	| "paid_only"
	| "unpaid_only"
	| "received_only"
	| "gender_status"
	| "male_only"
	| "female_only";

export interface StatisticsFilters {
	receptionDateRange?: DateRange;
	appointmentSlot?: string;
	/**
	 * Lives in the same object as the date filters on purpose: the page debounces
	 * this whole object, so a khoa change needs no second state and no second
	 * effect — which is what stops the two filters racing.
	 */
	facultyCode?: string;
	/**
	 * Admission cohort ("2026" / "2025"). Suy từ tiền tố `MaSV`, không phải cột
	 * DB — không có `admission_year`. Same reason as `facultyCode` for living in
	 * this shared object: one debounce, no second effect.
	 */
	admissionYear?: string;
}

interface StatisticsFilterControlsProps {
	filterType: StatisticsFilterType;
	onFilterChange: (type: StatisticsFilterType) => void;
	/** Chỉ cần khi `showChartLimit`; tab nào vẽ hết hạng mục thì bỏ trống. */
	chartLimit?: number;
	onChartLimitChange?: (limit: number) => void;
	showChartLimit?: boolean;
	/** Off for Tổng quan, which has no chart for the breakdown to apply to. */
	showFilterType?: boolean;
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
	showFilters?: boolean;
	/**
	 * Tắt riêng ô "Ngày tiếp nhận" mà vẫn giữ các bộ lọc khác. Tab "Nhịp độ" vẽ
	 * chính trục thời gian tiếp nhận, nên cắt bớt phạm vi ngày ở đó là cắt luôn
	 * thứ biểu đồ đang nói tới.
	 */
	showReceptionDateRange?: boolean;
}

export function StatisticsFilterControls({
	filterType,
	onFilterChange,
	chartLimit = 20,
	onChartLimitChange,
	showChartLimit = true,
	showFilterType = true,
	filters = {},
	onFiltersChange,
	showFilters = false,
	showReceptionDateRange = true,
}: StatisticsFilterControlsProps) {
	const filterOptions = [
		{ value: "total", label: "Tổng số sinh viên" },
		{ value: "enrollment_status", label: "Đã tiếp nhận - Chưa tiếp nhận" },
		{ value: "received_only", label: "Đã tiếp nhận" },
		{ value: "payment_status", label: "Chưa đóng học phí - Đã đóng học phí" },
		{ value: "paid_only", label: "Đã đóng học phí" },
		{ value: "unpaid_only", label: "Chưa đóng học phí" },
		{ value: "gender_status", label: "Nam - Nữ" },
		{ value: "male_only", label: "Nam" },
		{ value: "female_only", label: "Nữ" },
	];

	const chartLimitOptions = [5, 10, 15, 20, 25, 30, 50, 70];

	// Appointment is free text (e.g. "từ 8g00 đến 11g00 ngày 28/08/2025"), not
	// a date range -- the backend exposes its distinct values for exact-match
	// selection instead.
	const [appointmentSlots, setAppointmentSlots] = useState<string[]>([]);

	useEffect(() => {
		if (!showFilters) return;
		let cancelled = false;
		statisticsAPIService
			.getAppointmentSlots({ admissionYear: filters.admissionYear })
			.then((slots) => {
				if (!cancelled) setAppointmentSlots(slots);
			})
			.catch((err) => console.error("Failed to load appointment slots:", err));
		return () => {
			cancelled = true;
		};
	}, [showFilters, filters.admissionYear]);

	// More than one tab can mount these controls at once, so the id that ties the
	// label to its control has to be unique per instance rather than a constant.
	const slotFilterId = `${useId()}-slot`;

	// Một tab có thể tắt HẾT các control ở đây (tab "Học bạ", "Nhịp độ": không
	// breakdown, không giới hạn số mục, không lọc theo ngày) và khoá 2026 lại
	// không có lịch hẹn nào — khi đó `Card` bao ngoài vẫn hiện ra như một ô
	// trắng rỗng ở đầu tab, trông như một khối chưa tải xong. Không có gì để
	// điều khiển thì không dựng khung.
	const hasVisibleFilters =
		showFilters && !!onFiltersChange && (showReceptionDateRange || appointmentSlots.length > 0);
	if (!showFilterType && !showChartLimit && !hasVisibleFilters) return null;

	return (
		<Card>
			<CardContent className="p-3">
				<div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-start sm:items-end">
					{showFilterType && (
						<div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
							<label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
								Thống kê:
							</label>
							<Select
								value={filterType}
								onChange={(e) => onFilterChange(e.target.value as StatisticsFilterType)}
								className="h-8 w-full sm:w-48 px-2 text-xs"
							>
								{filterOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</Select>
						</div>
					)}

					{/* Chart Limit Selection */}
					{showChartLimit && (
						<div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
							<label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
								Hiển thị:
							</label>
							<div className="flex items-center gap-2">
								<Select
									value={chartLimit.toString()}
									onChange={(e) => onChartLimitChange?.(parseInt(e.target.value, 10))}
									className="h-8 w-16 px-2 text-xs"
								>
									{chartLimitOptions.map((limit) => (
										<SelectItem key={limit} value={limit.toString()}>
											{limit}
										</SelectItem>
									))}
								</Select>
								<span className="text-xs text-muted-foreground whitespace-nowrap">mục</span>
							</div>
						</div>
					)}

					{/* Ngày tiếp nhận và lịch hẹn — cùng một filter object. Khoa đã tách ra
					    `FacultyFilter` ở đầu trang: lựa chọn đó áp cho mọi tab, nên dựng lại
					    control trong từng tab sẽ hiện cùng một giá trị ở nhiều chỗ. */}
					{showFilters && onFiltersChange && (
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-3 w-full">
							{showReceptionDateRange && (
								<DateTimeRangePicker
									value={filters.receptionDateRange || {}}
									onChange={(range: DateRange) =>
										onFiltersChange({
											...filters,
											receptionDateRange: range,
										})
									}
									label="Ngày tiếp nhận"
									placeholder="Chọn ngày tiếp nhận"
									className="w-full min-w-0"
									mode="date"
								/>
							)}
							{/* Ẩn hẳn control khi danh sách lịch hẹn rỗng, thay vì để một Select
							    không có lựa chọn nào ngoài "Tất cả lịch hẹn". Khoá 2026 có cột
							    `ThoiGian` trống hoàn toàn nên `getAppointmentSlots` trả mảng rỗng
							    cho khoá đó — kiểm theo DỮ LIỆU THẬT thay vì hardcode năm "2026" để
							    quy tắc này còn đúng cả khi một khoá khác sau này cũng không dùng
							    lịch hẹn. Giá trị cũ được xoá ở `handleCohortChange` (StatisticsPage)
							    khi đổi khoá — ẩn mà không xoá thì một lịch hẹn chọn từ khoá trước
							    vẫn tiếp tục lọc trong im lặng, không có gì trên giao diện cho biết. */}
							{appointmentSlots.length > 0 && (
								<div className="flex flex-col gap-1 w-full min-w-0">
									<label
										htmlFor={slotFilterId}
										className="text-xs font-medium text-muted-foreground whitespace-nowrap"
									>
										Lịch hẹn
									</label>
									<Select
										id={slotFilterId}
										value={filters.appointmentSlot ?? ""}
										onChange={(e) =>
											onFiltersChange({
												...filters,
												appointmentSlot: e.target.value || undefined,
											})
										}
										className="h-9 text-xs"
									>
										<SelectItem value="">Tất cả lịch hẹn</SelectItem>
										{appointmentSlots.map((slot) => (
											<SelectItem key={slot} value={slot}>
												{slot}
											</SelectItem>
										))}
									</Select>
								</div>
							)}
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
}

interface PaginationControlsProps {
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
	hasNext: boolean;
	hasPrev: boolean;
	itemsPerPage: number;
	onItemsPerPageChange: (items: number) => void;
}

export function PaginationControls({
	currentPage,
	totalPages,
	onPageChange,
	hasNext,
	hasPrev,
	itemsPerPage,
	onItemsPerPageChange,
}: PaginationControlsProps) {
	const itemsPerPageOptions = [12, 24, 36, 48];

	return (
		<Card>
			<CardContent className="p-3 md:p-4">
				<div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
					{/* Items Per Page Selection */}
					<div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
						<label className="text-sm font-medium whitespace-nowrap">Hiển thị:</label>
						<div className="flex items-center gap-2">
							<Select
								value={itemsPerPage.toString()}
								onChange={(e) => onItemsPerPageChange(parseInt(e.target.value, 10))}
								className="h-8 w-20 px-2 py-1"
							>
								{itemsPerPageOptions.map((count) => (
									<SelectItem key={count} value={count.toString()}>
										{count}
									</SelectItem>
								))}
							</Select>
							<span className="text-sm text-muted-foreground whitespace-nowrap">
								mục trên trang
							</span>
						</div>
					</div>

					{/* Pagination Controls */}
					<div className="flex items-center justify-center gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => onPageChange(currentPage - 1)}
							disabled={!hasPrev}
							className="flex items-center gap-1"
						>
							<ChevronLeft className="h-4 w-4" />
							<span className="hidden sm:inline">Trước</span>
						</Button>

						<span className="text-xs sm:text-sm text-muted-foreground px-2 whitespace-nowrap">
							Trang {currentPage} / {totalPages}
						</span>

						<Button
							variant="outline"
							size="sm"
							onClick={() => onPageChange(currentPage + 1)}
							disabled={!hasNext}
							className="flex items-center gap-1"
						>
							<span className="hidden sm:inline">Sau</span>
							<ChevronRight className="h-4 w-4" />
						</Button>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
